import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import ParishRegistrationRequest from '../events/ParishRegistrationRequest';

type InviteBootstrap = {
  title: string;
  inviteeName?: string | null;
  inviteeEmail?: string | null;
  suggestedDomain?: string | null;
  enableCustomDomain?: boolean;
  branding: {
    headline?: string;
    description?: string;
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string | null;
    organizationHint?: string | null;
  };
};

/**
 * Landing pública de cadastro via convite do admin.
 * URL: /cadastro-personalizado?t=TOKEN
 */
const PersonalizedSignupPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteBootstrap | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Link inválido. Solicite um novo convite.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get('/public/custom-page-invite', { params: { t: token } })
      .then((res) => {
        if (cancelled) return;
        setInvite(res.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Convite inválido ou expirado.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const primary = invite?.branding?.primaryColor || '#0f4a80';
  const secondary = invite?.branding?.secondaryColor || '#1f6db0';

  const headerStyle = useMemo(
    () => ({
      background: `linear-gradient(120deg, ${primary} 0%, ${secondary} 100%)`,
      color: '#fff',
      borderRadius: 3,
      p: { xs: 2.5, md: 3.5 },
      mb: 3,
    }),
    [primary, secondary]
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invite) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error || 'Convite não encontrado.'}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        <Box sx={headerStyle}>
          <Stack spacing={1}>
            {invite.branding.organizationHint && (
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                {invite.branding.organizationHint}
              </Typography>
            )}
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {invite.branding.headline || invite.title}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.95, maxWidth: 640 }}>
              {invite.branding.description}
            </Typography>
            {invite.enableCustomDomain && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Inclui página com domínio personalizado após o cadastro
                {invite.suggestedDomain ? ` (ex.: ${invite.suggestedDomain})` : ''}.
              </Typography>
            )}
          </Stack>
        </Box>

        <ParishRegistrationRequest embedded customPageInviteToken={token} />
      </Container>
    </Box>
  );
};

export default PersonalizedSignupPage;
